import { useState } from "react";
import { Button } from "@cofano/cck";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import DocumentDrawer from "components/documents/DocumentDrawer";
import { Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import { intl } from "LocaleWrapper";
import { hasPluginPermission } from "components/utils/Permission";
import { useStoreState } from "ezStore";

export type TransportDocumentCargo = {
	id: number,
	customerTransportDocument?: { id: number } | null,
};

enum Type {
	Normal,
	Empty,
	Customer
}

export type TransportDocumentMenuProps = {
	cargo: TransportDocumentCargo,
	anchorEl: HTMLElement | null,
	onClose: () => void,
	onAddCustomerDocument?: () => void,
	onRemoveCustomerDocument?: () => void,
};

export const TransportDocumentMenu = ({
	cargo,
	anchorEl,
	onClose,
	onAddCustomerDocument,
	onRemoveCustomerDocument,
}: TransportDocumentMenuProps) => {

	const session = useStoreState(store => store.session);
	const [ documentDrawer, setDocumentDrawer ] = useState({ open: false, type: Type.Normal });

	const showTransportDocument = (type: Type) => {
		onClose();
		setDocumentDrawer({ open: true, type: type });
	};

	const runAction = (action: () => void) => {
		onClose();
		action();
	};

	const showAddCustomerDocument = onAddCustomerDocument != null && cargo.customerTransportDocument == null;
	const showRemoveCustomerDocument = onRemoveCustomerDocument != null && cargo.customerTransportDocument != null;

	return (
		<>
			<Menu
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				onClose={onClose}
				disableRestoreFocus
			>
				<div style={{ padding: "8px" }}>
					{cargo.customerTransportDocument && (
						<MenuItem
							onClick={() => showTransportDocument(Type.Customer)}
							sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
						>
							{intl.formatMessage({ id: "CUSTOMER_TRANSPORT_DOCUMENT" })}
							{showRemoveCustomerDocument && (
								<Tooltip title={intl.formatMessage({ id: "REMOVE_CUSTOMER_TRANSPORT_DOCUMENT" })}>
									<Button
										icon={<DeleteIcon fontSize="small" />}
										variant="text"
										color="neutral"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											runAction(onRemoveCustomerDocument!);
										}}
									/>
								</Tooltip>
							)}
						</MenuItem>
					)}
					<MenuItem onClick={() => showTransportDocument(Type.Normal)}>
						{intl.formatMessage({ id: "TRANSPORT_DOCUMENT" })}
					</MenuItem>
					{hasPluginPermission(session, "cargoTransportDocumentLatestVersion") && (
						<MenuItem onClick={() => showTransportDocument(Type.Empty)}>
							{intl.formatMessage({ id: "TRANSPORT_DOCUMENT_EMPTY" })}
						</MenuItem>
					)}

					{showAddCustomerDocument && <Divider />}

					{showAddCustomerDocument && (
						<div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px" }}>
							<Button
								icon={<NoteAddIcon fontSize="small" />}
								variant="outlined"
								size="small"
								onClick={() => runAction(onAddCustomerDocument!)}
							>
								{intl.formatMessage({ id: "ADD_CUSTOMER_TRANSPORT_DOCUMENT" })}
							</Button>
						</div>
					)}
				</div>
			</Menu>

			<DocumentDrawer
				open={documentDrawer.open}
				onClose={() => setDocumentDrawer({ open: false, type: Type.Normal })}
				queryParams={{ empty: documentDrawer.type == Type.Empty ? true : undefined }}
				previewUrl={documentDrawer.type == Type.Customer
					? `/documents/preview/${cargo.customerTransportDocument!.id}`
					: `cargos/${cargo.id}/transportDocument`}
				url={documentDrawer.type == Type.Customer
					? `/documents/download/${cargo.customerTransportDocument!.id}`
					: `cargos/${cargo.id}/transportDocument`}
			/>
		</>
	);
};

export type TransportDocumentPdfButtonProps = {
	cargo: TransportDocumentCargo,
};

export const TransportDocumentPdfButton = ({ cargo }: TransportDocumentPdfButtonProps) => {

	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);

	return (
		<div>
			<Button
				onClick={(e) => setAnchorEl(e.currentTarget)}
				variant="text"
				size="small"
			>
				<InsertDriveFileIcon fontSize="small" />
			</Button>

			<TransportDocumentMenu
				cargo={cargo}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
			/>
		</div>
	);
};
